import React, { useEffect, useState } from 'react';
import { ShoppingBag, ExternalLink, AlertCircle } from 'lucide-react';
import { getPlayerProfile } from '../../../../services/firestoreService';
import { db } from '../../../../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * ShopEditor - Editor component for the shop block
 * Shows product count and link to manage products
 */
const ShopEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadShopInfo = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get profile to get vendorId (which is the userId)
        const profile = await getPlayerProfile(profileId);
        const vendorId = profile?.userId || profileId;

        // Count active products
        const q = query(
          collection(db, 'products'),
          where('vendorId', '==', vendorId),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        setProductCount(snapshot.size);
      } catch (error) {
        console.error('Error loading shop info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadShopInfo();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <ShoppingBag size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Shop</h4>
            {productCount > 0 ? (
              <p className="text-sm text-gray-400">
                {productCount} active {productCount === 1 ? 'product' : 'products'} will display here
              </p>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertCircle size={14} />
                <span>No active products. Add products to your shop!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Shop"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Max Products */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Maximum Products to Show
        </label>
        <select
          value={content.maxProducts || 6}
          onChange={(e) => handleChange('maxProducts', parseInt(e.target.value))}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
        >
          <option value={3}>3 products</option>
          <option value={4}>4 products</option>
          <option value={6}>6 products</option>
          <option value={8}>8 products</option>
          <option value={12}>12 products</option>
        </select>
      </div>

      {/* Show Prices Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Show Prices</p>
          <p className="text-xs text-gray-500">Display product prices on cards</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.showPrices !== false}
            onChange={(e) => handleChange('showPrices', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['shop']}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['shop']}
          theme={theme || {}}
          blockType="shop"
        />
      </>)}

      {/* Manage Shop Link */}
      <a
        href="/dashboard/website/shop"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Shop
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default ShopEditor;
